/**
 * Read the error an OAuth provider hands back on the callback URL.
 *
 * When Google rejects the client — `invalid_client`, a 401 that means the
 * client id and secret Supabase forwards no longer match a live Google OAuth
 * client — the browser is redirected to /auth/callback carrying `error` and
 * `error_description` and no code. The callback used to look only for a code or
 * tokens, find neither, and report a generic failure, discarding the one piece
 * of information that identifies the fault.
 *
 * Errors arrive on the query string for the PKCE flow and in the hash fragment
 * for implicit responses, so both are read.
 */

/** Provider codes worth translating for whoever is staring at the screen. */
const KNOWN_CAUSES = {
  invalid_client:
    "the identity provider rejected this site's OAuth client — its client ID and secret need to be re-synced",
  unauthorized_client: "this OAuth client is not authorised for the requested flow",
  redirect_uri_mismatch:
    "the redirect URL is not on the provider's allow-list for this OAuth client",
  access_denied: "sign-in was cancelled",
  server_error: "the identity provider reported an internal error",
  temporarily_unavailable: "the identity provider is temporarily unavailable",
};

function fromParams(params) {
  if (!params) return null;
  const code = params.get("error") || params.get("error_code");
  if (!code) return null;
  return {
    code,
    description: (params.get("error_description") || "").replace(/\+/g, " ").trim(),
  };
}

/**
 * @param {string} search Location search, e.g. "?error=invalid_client".
 * @param {string} hash   Location hash, e.g. "#error=access_denied".
 * @returns {{code: string, description: string, message: string}|null}
 */
export function readOAuthError(search = "", hash = "") {
  const found =
    fromParams(new URLSearchParams((search || "").replace(/^\?/, ""))) ||
    fromParams(new URLSearchParams((hash || "").replace(/^#/, "")));
  if (!found) return null;

  const cause = KNOWN_CAUSES[found.code];
  const detail = found.description || cause || "";
  return {
    ...found,
    message: detail
      ? `Google sign-in failed (${found.code}): ${detail}`
      : `Google sign-in failed (${found.code})`,
  };
}
